import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { istanbulDayString, istanbulHourOf, t } from '@aquabox/shared';
import { useToast } from '../components/Toast';
import { isStaleOpenTrip, OPEN_TRIP_STATUSES } from '../lib/stale-trip';
import type {
  ReloadRequest,
  Trip,
  TripDetail,
  TripSummary,
  TripVoidResponse,
  AccountsSummary,
  CashMove,
  CashPoint,
  CashSummary,
  CustomerDepositRow,
  CustomerOrderSummary,
  CustomerTxn,
  DriverFloatRow,
  FloatSummary,
  Receipt,
  ReceiptDetail,
  ForeignAccount,
  StockLevel,
  StockMove,
  VehicleStock,
  VehiclePosition,
  Channel,
  ChannelSession,
  PullSource,
  ProductMapping,
  MappingSuggestion,
  ScraperStatus,
  HistoryImportResult,
  ReportSummary,
  AuthUser,
  Brand,
  Customer,
  Dashboard,
  Order,
  AppNotification,
  DriverPerformance,
  DepositReport,
  FuelLogList,
  Page,
  Product,
  Region,
  Vehicle,
  Driver,
  DriverWithPin,
  TenantState,
  AutoAssignGate,
  ImportReport,
  HayatVehiclesResponse,
  HayatVehicleMapEntry,
  LedgerPolicy,
  ExternalLedgerPreview,
  ExternalLedgerPostResponse,
  ChannelConfig,
  DryRunResult,
} from './types';

/** Genel Bakış: tek toplu istek (YOL-HARITASI §0.1). 30 sn'de bir tazelenir. */
export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Dashboard>('/api/dashboard'),
    refetchInterval: 30_000,
  });
}

/**
 * Bildirimler (§2.3 zil ikonu).
 *
 * Prototipte zil STATİKTİ ve kırmızı nokta hep duruyordu. Faz 8'de bildirimlerin gerçek bir
 * üreticisi olduğu için (ara yükleme talebi, farkla kapanan sefer) zil artık gerçek listeyi
 * gösterir. Panel ve patron uygulaması AYNI ucu okur; birinde okunan öbüründe de okunmuş olur.
 */
export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<Page<AppNotification>>('/api/notifications?pageSize=30'),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/notifications/${id}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/notifications/read-all', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/**
 * Kendine test bildirimi gönder (`POST /api/notifications/test`).
 *
 * Sunucu bildirimi çağıranın KENDİSİNE yazar (`userId` yalnız oturumdan gelir — istek
 * gövdesinde başkasına yazdırma imkânı yoktur). Aynı kullanıcı 60 saniye içinde ikinci kez
 * çağırırsa sunucu 429 döner; o hatanın mesajı SUNUCUNUN KENDİ metnidir ve panelde
 * olduğu gibi gösterilir.
 *
 * Başarıda bildirim listesi tazelenir: yeni satır zil düğmesinde görünsün.
 */
export function useSendTestNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ id: string }>('/api/notifications/test', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/** Şoför performansı: km beyanı / GPS karşılaştırması, mazot, L/100km (§3.15). */
export function useDriverPerformance(dateFilter: 'today' | 'week' | 'all') {
  return useQuery({
    queryKey: ['driver-performance', dateFilter],
    queryFn: () => api.get<DriverPerformance>(`/api/drivers/performance?dateFilter=${dateFilter}`),
    refetchInterval: 60_000,
  });
}

/** Zimmet / depozito raporu (patron): `GET /api/reports/deposits`. Yalnız okunur. */
export function useDepositReport() {
  return useQuery({
    queryKey: ['reports', 'deposits'],
    queryFn: () => api.get<DepositReport>('/api/reports/deposits'),
  });
}

/**
 * Mazot dolumları (§3.15 D).
 *
 * Yanıt `items`in yanında `total` (para) ve `count` (satır sayısı) taşır; ikisi de FİLTRELENMİŞ
 * KÜMENİN TAMAMINDAN gelir. `items` sunucuda 200 satırda kesildiği için panel ne toplar ne sayar.
 */
export function useFuelLogs(dateFilter: 'today' | 'week' | 'all') {
  return useQuery({
    queryKey: ['fuel-logs', dateFilter],
    queryFn: () => api.get<FuelLogList>(`/api/fuel-logs?dateFilter=${dateFilter}`),
  });
}

export function useAddFuelLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/api/fuel-logs', body),
    onSuccess: () => {
      // Dolum kasaya da yazılır; iki ekranın rakamları ayrışmasın diye ikisi de tazelenir.
      void qc.invalidateQueries({ queryKey: ['fuel-logs'] });
      void qc.invalidateQueries({ queryKey: ['driver-performance'] });
      void qc.invalidateQueries({ queryKey: ['cash-summary'] });
      void qc.invalidateQueries({ queryKey: ['cash-moves'] });
    },
  });
}

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: () => api.get<AuthUser>('/api/auth/me') });
}

/**
 * Sipariş listesi.
 *
 * `refetchInterval` 15 sn: kanal siparişleri sunucuya çekilince panelin kendiliğinden yenilenmesi
 * için (görev: "panel yeni sipariş anında görünsün"). `refetchIntervalInBackground` sekme arka
 * plandayken de yenilenir — patron panelden çıkıp başka işle uğraşırken gelen siparişi kaçırmasın.
 * `NewOrderWatcher` aynı ucu kullanır ve ekrana gerçek zamanlı bildirim düşürür.
 */
export function useOrders(params: Record<string, string | undefined>, enabled = true) {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) query.set(k, v);
  const qs = query.toString();
  return useQuery({
    queryKey: ['orders', qs],
    queryFn: () => api.get<Page<Order>>(`/api/orders${qs ? `?${qs}` : ''}`),
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    enabled,
  });
}

/** Tek sipariş detayı — detay modalı açıkken daha seykinli tazeleme yeter. */
export function useOrder(id: string | null) {
  return useQuery({
    queryKey: ['order', id],
    queryFn: () => api.get<Order>(`/api/orders/${id!}`),
    enabled: id !== null,
    refetchInterval: 30_000,
  });
}

export function useCustomers(params: Record<string, string | undefined>) {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) query.set(k, v);
  const qs = query.toString();
  return useQuery({
    queryKey: ['customers', qs],
    queryFn: () => api.get<Page<Customer>>(`/api/customers${qs ? `?${qs}` : ''}`),
  });
}

/** KPI toplamları: sayfalanmış listeden DEĞİL, TÜM aktif müşteriler üzerinden (bkz. customers.controller.ts#summary). */
export function useCustomerSummary() {
  return useQuery({
    queryKey: ['customers', 'summary'],
    queryFn: () =>
      api.get<{ totalCustomers: number; totalReceivable: string; totalJugsOnHand: number }>(
        '/api/customers/summary',
      ),
  });
}

export function useCustomer(id: string | null) {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: () => api.get<Customer>(`/api/customers/${id!}`),
    enabled: id !== null,
  });
}

/**
 * Müşterinin sipariş geçmişi özeti (§3.8 D.6 — "Sipariş alışkanlığı" kutusu).
 *
 * Query anahtarı `['customers', id, 'order-summary']`. `useFinanceMutations` (tahsilat)
 * `['customers']` önekini invalidate ediyor (`queries.ts` içinde `invalidateQueries`
 * varsayılan `exact: false`), bu yüzden tahsilat sonrası bu sorgu da tazelenir. Sipariş
 * mutasyonları (`useOrderMutations` — oluşturma/atama/İPTAL) yalnız `['orders']` ve
 * `['dashboard']`'ı invalidate ediyor, `['customers']`'ı DOKUNMUYOR — yeni sipariş ya da
 * iptalden sonra bu özet o mutasyonla değil, 60 sn'lik `refetchInterval` ile tazelenir.
 * `useOrders` (OrderHistory listesi) ise kendi 15 sn'siyle daha çabuk günceller.
 */
export function useCustomerOrderSummary(customerId: string | null) {
  return useQuery({
    queryKey: ['customers', customerId, 'order-summary'],
    queryFn: () => api.get<CustomerOrderSummary>(`/api/customers/${customerId!}/order-summary`),
    enabled: customerId !== null,
    refetchInterval: 60_000,
  });
}

/**
 * Müşteri yazma işlemleri (§3.8 panel ekranı).
 *
 * `create` ve `update` gövdesi sunucunun şemasıyla birebir (apps/api/customers/customers.schema.ts):
 * - Zorunlu (create): `fullName`, `phone`, `address`.
 * - İsteğe bağlı: `phone2`, `addressNote`, `regionId`, `notes`. `regionId` verilmezse bölge
 *   adresten otomatik bulunur (sunucu kuralı).
 * - Update'te bütün alanlar isteğe bağlı; ayrıca `isActive: true` ile geri açma.
 *
 * Boş isteğe bağlı alanlar gönderilmez — sunucu zod şeması boş string'i kabul etmez, panel
 * hiç yazmazsa `undefined` gider ve doğrulama geçer. Bu yüzden çağıran yerde trim'lenmiş
 * boş string yerine `undefined` yazılır (Customers.tsx form deseni). Önceden dolu olup
 * kullanıcı tarafından boşaltılan alanlar `null` gider (sunucu DB'ye NULL yazar); yoksa
 * eski değer sessizce kalırdı.
 *
 * `deactivate` `DELETE /api/customers/:id` uçtan yürür: sunucu kural 7 (bakiye + zimmet sıfır)
 * kontrolünü burada uygular. PATCH ile `isActive: false` sunucuda 400 ile reddedilir — PATCH
 * yolu kontrolü atlar ve KVKK anonimleştirme ön koşulunu delerdi.
 *
 * Başarıda liste + detay + KPI tazelenir: çekmeceye yansımayan güncel bakiye patronun
 * gözünden kaçar.
 */
export function useCustomerMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['customers'] });
    await qc.invalidateQueries({ queryKey: ['customers', 'summary'] });
    await qc.invalidateQueries({ queryKey: ['customer'] });
  };
  return {
    create: useMutation({
      mutationFn: (v: Record<string, unknown>) => api.post<Customer>('/api/customers', v),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...v }: { id: string } & Record<string, unknown>) =>
        api.patch<Customer>(`/api/customers/${id}`, v),
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: (id: string) => api.delete<Customer>(`/api/customers/${id}`),
      onSuccess: invalidate,
    }),
    reactivate: useMutation({
      mutationFn: (id: string) => api.patch<Customer>(`/api/customers/${id}`, { isActive: true }),
      onSuccess: invalidate,
    }),
  };
}

export function useProducts(params: { brandId?: string } = {}) {
  const query = new URLSearchParams();
  if (params.brandId) query.set('brandId', params.brandId);
  const qs = query.toString();
  return useQuery({
    queryKey: ['products', qs],
    queryFn: () => api.get<Page<Product>>(`/api/products${qs ? `?${qs}` : ''}`),
  });
}

/**
 * Marka listesi. Tüm roller okuyabilir; yazma yalnız OWNER/MANAGER — `useBrandMutations`
 * patron/yönetici ekranına bağlanır.
 */
export function useBrands() {
  return useQuery({
    queryKey: ['brands'],
    queryFn: () => api.get<Brand[]>('/api/brands'),
  });
}

export function useBrandMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['brands'] });
    await qc.invalidateQueries({ queryKey: ['products'] });
    await qc.invalidateQueries({ queryKey: ['products-overview'] });
  };
  return {
    create: useMutation({
      mutationFn: (v: { name: string; code: string }) => api.post<Brand>('/api/brands', v),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({
        id,
        ...v
      }: { id: string } & Partial<Pick<Brand, 'name' | 'code' | 'isActive'>>) =>
        api.patch<Brand>(`/api/brands/${id}`, v),
      onSuccess: invalidate,
    }),
  };
}

export function useVehicles() {
  return useQuery({
    queryKey: ['vehicles'],
    queryFn: () => api.get<Page<Vehicle>>('/api/vehicles'),
  });
}

/**
 * Ürün yazma işlemleri (`urunler`).
 *
 * `deactivate` SİLMEZ: sunucu `DELETE`'i pasifleştirme olarak uygular. Geçmiş sipariş ve defter
 * satırları ürüne bağlı; kaydı kaldırmak dünkü teslimatı okunamaz hale getirirdi.
 *
 * Fiyat değişince stok seviyeleri ve pano da tazelenir: ikisi de ürün adını ve fiyatını gösterir.
 */
export function useProductMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['products'] });
    // Ürünler ekranı 'products-overview' anahtarını okur; yazma sonrası liste bayat kalmasın.
    await qc.invalidateQueries({ queryKey: ['products-overview'] });
    await qc.invalidateQueries({ queryKey: ['stock-levels'] });
  };
  return {
    create: useMutation({
      mutationFn: (v: Record<string, unknown>) => api.post('/api/products', v),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...v }: { id: string } & Record<string, unknown>) =>
        api.patch(`/api/products/${id}`, v),
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: (id: string) => api.delete(`/api/products/${id}`),
      onSuccess: invalidate,
    }),
  };
}

/** Ekip listesi (§3.14). Yalnız patron/yönetici okuyabilir; şoför diğerlerinin telefonunu göremez. */
export function useDrivers() {
  return useQuery({
    queryKey: ['drivers'],
    queryFn: () => api.get<Page<Driver>>('/api/drivers?pageSize=100'),
  });
}

/**
 * Araç yazma işlemleri (§3.14).
 *
 * `deactivate` araç kaydını SİLMEZ, pasifleştirir: geçmiş seferler ve stok hareketleri araç id'sine
 * bağlı. Ekip listesi de tazelenir — zimmetli şoförün kartı aracın plakasını gösteriyor.
 */
export function useVehicleMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['vehicles'] });
    await qc.invalidateQueries({ queryKey: ['drivers'] });
  };
  return {
    create: useMutation({
      mutationFn: (v: Record<string, unknown>) => api.post<Vehicle>('/api/vehicles', v),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...v }: { id: string } & Record<string, unknown>) =>
        api.patch<Vehicle>(`/api/vehicles/${id}`, v),
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: (id: string) => api.delete(`/api/vehicles/${id}`),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Şoför yazma işlemleri (§3.14).
 *
 * `create` ve `resetPin` yanıtları DÜZ METİN PIN taşır ve bu tek fırsattır — sunucuda yalnız
 * özeti duruyor. Bu yüzden çağıran PIN'i göstermeden başarıyı "tamam" saymamalı; kaybolursa
 * tek çözüm yeniden sıfırlamaktır.
 */
export function useDriverMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['drivers'] });
    await qc.invalidateQueries({ queryKey: ['vehicles'] });
  };
  return {
    create: useMutation({
      mutationFn: (v: Record<string, unknown>) => api.post<DriverWithPin>('/api/drivers', v),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...v }: { id: string } & Record<string, unknown>) =>
        api.patch<Driver>(`/api/drivers/${id}`, v),
      onSuccess: invalidate,
    }),
    resetPin: useMutation({
      mutationFn: (id: string) =>
        api.post<{ id: string; pin: string }>(`/api/drivers/${id}/reset-pin`, {}),
    }),
    deactivate: useMutation({
      mutationFn: (id: string) => api.delete(`/api/drivers/${id}`),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Panel araç/çalışan eşleme verisi — mevcut eşleme, gözlem ve panelin canlı bilinenleri.
 *
 * HAYAT_PANEL ve ABANT_PANEL için anlamlı; başka kaynak → 400 (sunucu). Sorgu `enabled`
 * koşulu ekranın yetkisiyle sınırlandırılır (ekran yalnız bu iki kaynağın kartlarını gösterir).
 */
export function useHayatVehicles(source: string | undefined) {
  return useQuery({
    queryKey: ['hayat-vehicles', source],
    queryFn: () => api.get<HayatVehiclesResponse>(`/api/integrations/${source}/vehicles`),
    enabled: source === 'HAYAT_PANEL' || source === 'ABANT_PANEL',
  });
}

/**
 * Panel etiketi → bizim araç eşleme arayışı (Siparişler tablosu + sipariş detayı, paket
 * "panel sade"). `live=0` querystring'i CANLI panel okumasını TETİKLEMEZ — yalnız kayıtlı
 * `vehicleMap`i okur; `useHayatVehicles` (Entegrasyonlar kartı) ile AYNI query anahtarını
 * PAYLAŞMAZ, çakışıp birbirinin canlı sonucunu ezmesin diye ayrı tutulur.
 *
 * Yalnız OWNER'da sorgu atılır (uç zaten MANAGER/DRIVER'ı 403'ler) — rol MANAGER ise eşleme
 * hep boş sayılır, ekran yalnız ham etiketi gösterir. İstek hata verirse de aynı sonuç:
 * ekran kırılmaz, eşleme boş sayılır.
 *
 * `source`a göre genelleşir (HAYAT_PANEL, ABANT_PANEL) — `useHayatVehicleLookup` bunun
 * üstünde ince bir sarmalayıcıdır (geri uyum: mevcut çağıranlar değişmedi).
 */
export function usePanelVehicleLookup(source: 'HAYAT_PANEL' | 'ABANT_PANEL') {
  const me = useMe();
  const isOwner = me.data?.role === 'OWNER';
  const panel = useQuery({
    queryKey: ['hayat-vehicles', source, 'lookup'],
    queryFn: () => api.get<HayatVehiclesResponse>(`/api/integrations/${source}/vehicles?live=0`),
    enabled: isOwner,
    retry: false,
  });
  const vehicles = useVehicles();

  return useMemo(() => {
    const vehicleIdByLabel = new Map<string, string>();
    for (const m of panel.data?.map ?? []) vehicleIdByLabel.set(m.hayatLabel, m.vehicleId);
    const plateById = new Map<string, string>();
    for (const v of vehicles.data?.items ?? []) plateById.set(v.id, v.plate);
    return (label: string | null | undefined): { plate: string; label: string } | null => {
      if (!label) return null;
      const vehicleId = vehicleIdByLabel.get(label);
      if (!vehicleId) return null;
      const plate = plateById.get(vehicleId);
      if (!plate) return null;
      return { plate, label };
    };
  }, [panel.data, vehicles.data]);
}

/** `usePanelVehicleLookup('HAYAT_PANEL')` — geri uyum için ayrı ad korunur. */
export function useHayatVehicleLookup() {
  return usePanelVehicleLookup('HAYAT_PANEL');
}

/**
 * ABANT_PANEL çalışan kimliği → "Ad Soyad" (`config.employeeNames`). Siparişler/Sipariş detayı
 * ekranının "Abant: Ad Soyad" gösterimi için — `usePanelVehicleLookup('ABANT_PANEL')` ile AYNI
 * query anahtarını kullanır, react-query önbelleği paylaşılır (ikinci bir istek gitmez).
 */
export function useAbantEmployeeNames(): Record<string, string> {
  const me = useMe();
  const isOwner = me.data?.role === 'OWNER';
  const q = useQuery({
    queryKey: ['hayat-vehicles', 'ABANT_PANEL', 'lookup'],
    queryFn: () => api.get<HayatVehiclesResponse>('/api/integrations/ABANT_PANEL/vehicles?live=0'),
    enabled: isOwner,
    retry: false,
  });
  return q.data?.employeeNames ?? {};
}

/**
 * Hayat `vehicleMap` kaydı — `vehicleId → hayatLabel` tablosunu sunucuya yazar.
 *
 * Eşleme listesi (vehicles, drivers) ve Hayat eşleme yanıtı tazelenir: eşleme değişince
 * Hayat kartı ve ekip listesi tutarlı kalmalı.
 */
export function useSaveHayatVehicleMap(source: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (map: Record<string, string>) =>
      api.put<{ map: HayatVehicleMapEntry[] }>(`/api/integrations/${source}/vehicle-map`, map),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['hayat-vehicles', source] });
      await qc.invalidateQueries({ queryKey: ['vehicles'] });
      await qc.invalidateQueries({ queryKey: ['drivers'] });
    },
  });
}

/** Kiracı ayarları, bayrakları ve erişim durumu. */
export function useTenant() {
  return useQuery({ queryKey: ['tenant'], queryFn: () => api.get<TenantState>('/api/tenant') });
}

/** Otomatik atama kapısı (uyum + gün sayısı). `enabled` yalnız ilgili role için açılır. */
export function useAutoAssignGate(enabled = true) {
  return useQuery({
    queryKey: ['tenant', 'auto-assign-gate'],
    queryFn: () => api.get<AutoAssignGate>('/api/tenant/auto-assign-gate'),
    enabled,
  });
}

/**
 * Ayarlar yazma işlemleri.
 *
 * `updateFlags` KILL SWITCH'i yazar (kural 10). Başarıda pano da tazelenir: erişim durumu ve
 * çekim bayrakları orada da görünüyor, iki ekran ayrı şey söylememeli.
 *
 * PATCH KISMİDİR: yalnız değişen alanlar gönderilir — sunucu şeması gövdede olmayan alanı
 * varsayılana çekmemek için özellikle `removeDefault()` ile kurulmuş (tenant-settings.schema.ts).
 *
 * `updateHayatWriteMode` PATCH /tenant/hayat-write-mode (yalnız OWNER) için ayrı uçtur. Kullanıcı
 * yalnız patron olduğunda çağırmalı; ekran görünürlüğü Settings.tsx'te `role==='OWNER'` ile sınırlı.
 * `updateAssignmentMode` PATCH /tenant/settings üzerinden `orderAssignmentMode` alanını yazar
 * (panel §3.6): bölge kuralıyla otomatik atama AÇIK/KAPALI.
 */
export function useTenantMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['tenant'] });
    await qc.invalidateQueries({ queryKey: ['dashboard'] });
    await qc.invalidateQueries({ queryKey: ['scrapers'] });
  };
  return {
    updateSettings: useMutation({
      mutationFn: (
        v: Record<
          string,
          number | string | Record<string, string> | { start: string; end: string }
        >,
      ) => api.patch<TenantState>('/api/tenant/settings', v),
      onSuccess: invalidate,
    }),
    updateFlags: useMutation({
      mutationFn: (v: Record<string, boolean>) => api.patch<TenantState>('/api/tenant/flags', v),
      onSuccess: invalidate,
    }),
    updateHayatWriteMode: useMutation({
      mutationFn: (v: {
        mode: 'SHADOW' | 'CONFIRM' | 'AUTO';
        confirmMode: 'SHADOW' | 'CONFIRM' | 'AUTO';
      }) => api.patch<TenantState>('/api/tenant/hayat-write-mode', v),
      onSuccess: invalidate,
    }),
    updateAssignmentMode: useMutation({
      mutationFn: (v: 'AUTO' | 'MANUAL') =>
        api.patch<TenantState>('/api/tenant/settings', { orderAssignmentMode: v }),
      onSuccess: invalidate,
    }),
  };
}

export function useRegions() {
  return useQuery({ queryKey: ['regions'], queryFn: () => api.get<Page<Region>>('/api/regions') });
}

/**
 * Bölge yazma işlemleri (Faz 1.1).
 *
 * `deactivate` SİLMEZ, pasifleştirir: mahalle listesi başka bölgeye taşınmadan silinemez, bu
 * yüzden deaktif bölge müşteri eşlemesine katılmaz ama kaydı durur (kural 1).
 *
 * Adres eşleme (Faz 1.4) ayrı ucu okur; liste tazelenmez çünkü sonuç ekrana yazılır, depoya
 * değil — yeniden istek atmak aynı adres için yarış yaratır.
 */
// ── Faz 8: Hayat entegrasyonu — yazma denemesi (paket H) ──

/**
 * Tek denemenin sunucu sonucu (POST /api/orders/:id/hayat-push yanıtı).
 * `reason` yalnız BLOCKED'da; `action` tüm sonuçlarda doludur (uzlaştırıcı bunu kayıt için de
 * taşır). Bu tipten panele/mobile'a SADECE t() üzerinden metin düşer; sabitler shared paketinden
 * gelir (CHANNEL_PUSH_RESULTS / CHANNEL_PUSH_BLOCK_REASONS).
 */
export type HayatPushAction =
  'CONFIRM_VEHICLE' | 'SEND_TO_VEHICLE' | 'MARK_DELIVERED' | 'CHANGE_VEHICLE' | 'CANCEL';

export interface HayatPushOutcome {
  result:
    | 'SENT'
    | 'SKIPPED_ALREADY_DONE'
    | 'SHADOW_ONLY'
    | 'BLOCKED'
    | 'FAILED'
    | 'UNCERTAIN'
    | 'PENDING';
  reason?: string;
  action?: 'CONFIRM_VEHICLE' | 'SEND_TO_VEHICLE' | 'MARK_DELIVERED' | 'CHANGE_VEHICLE' | 'CANCEL';
  error?: string;
}

/** Bir denemenin özeti (panel sipariş detayı yanında gösterilir). */
export interface HayatPushAttempt {
  id: string;
  action: 'CONFIRM_VEHICLE' | 'SEND_TO_VEHICLE' | 'MARK_DELIVERED' | 'CHANGE_VEHICLE' | 'CANCEL';
  mode: 'SHADOW' | 'CONFIRM' | 'AUTO';
  trigger: 'SCHEDULER' | 'BUTTON';
  result:
    | 'SENT'
    | 'SKIPPED_ALREADY_DONE'
    | 'SHADOW_ONLY'
    | 'BLOCKED'
    | 'FAILED'
    | 'UNCERTAIN'
    | 'PENDING';
  error: string | null;
  vehicleLabel: string | null;
  /** Y1b: yazıcının aldığı HTTP kodu (yoksa null). Ham gözlem (observedRaw) KVKK gereği yanıtta yoktur. */
  httpStatus: number | null;
  jobId: string | null;
  durationMs: number | null;
  createdAt: string;
}

export function useHayatPushAttempts(orderId: string | null) {
  return useQuery({
    queryKey: ['hayat-push-attempts', orderId],
    queryFn: () => api.get<HayatPushAttempt[]>(`/api/orders/${orderId!}/hayat-push-attempts`),
    enabled: orderId !== null,
    staleTime: 10_000,
  });
}

export function useHayatPushMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, action }: { orderId: string; action: HayatPushAction }) =>
      api.post<HayatPushOutcome>(`/api/orders/${orderId}/hayat-push`, { action }),
    onSuccess: (_data, { orderId }) => {
      void qc.invalidateQueries({ queryKey: ['hayat-push-attempts', orderId] });
      void qc.invalidateQueries({ queryKey: ['order', orderId] });
    },
  });
}

export function useRegionMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['regions'] });
  };
  return {
    create: useMutation({
      mutationFn: (v: Record<string, unknown>) =>
        api.post<Region & { rematch: RegionRematchSummary | null }>('/api/regions', v),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ id, ...v }: { id: string } & Record<string, unknown>) =>
        api.patch<Region & { rematch: RegionRematchSummary | null }>(`/api/regions/${id}`, v),
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: (id: string) => api.delete<Region>(`/api/regions/${id}`),
      onSuccess: invalidate,
    }),
  };
}

/** POST /api/regions/archive-all: tüm bölgeleri arşivler (silmez), yalnız OWNER. */
export interface ArchiveAllResult {
  archivedRegions: number;
  detachedCustomers: number;
  archivedRules: number;
}

export function useArchiveAllRegions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ArchiveAllResult>('/api/regions/archive-all', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
      void qc.invalidateQueries({ queryKey: ['region-suggestions'] });
    },
  });
}

/** POST /api/regions/rematch yanıt gövdesi. */
export interface RegionRematchSummary {
  checked: number;
  regionSet: number;
  assigned: number;
  unchanged: number;
  skipped: number;
}

/** Panelin üst çubuğundaki "Bekleyen siparişleri yeniden dağıt" düğmesi. */
export function useRematchPending() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<RegionRematchSummary>('/api/regions/rematch', {}),
    onSuccess: () => {
      // Bölge değişmiş olabilir; siparişler ve bölgeler listesi tazelensin.
      void qc.invalidateQueries({ queryKey: ['regions'] });
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

export function useMatchAddress() {
  return useMutation({
    mutationFn: (address: string) =>
      api.get<{
        region: { id: string; name: string } | null;
        vehicle: { id: string; plate: string } | null;
        autoAssign: boolean;
      }>(`/api/regions/match?address=${encodeURIComponent(address)}`),
  });
}

/** Sipariş yazma işlemleri: başarıda liste ve pano tazelenir. */
/** Seferler listesi (§3.9). */
export function useTrips(params: Record<string, string | undefined>) {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) query.set(k, v);
  const qs = query.toString();
  return useQuery({
    queryKey: ['trips', qs],
    queryFn: () => api.get<Page<Trip>>(`/api/trips${qs ? `?${qs}` : ''}`),
  });
}

/**
 * Açık seferler kartları (Seferler ekranının üstü). Tarih filtresinden BAĞIMSIZdır: dün açılıp
 * hâlâ kapanmamış sefer de kart olarak görünmelidir. 15 sn'de bir yenilenir; sekme gizliyken
 * durur (`refetchIntervalInBackground` varsayılanı `false`).
 */
export function useLiveTrips() {
  return useQuery({
    queryKey: ['trips', 'live'],
    // Sayfa sınırı 100: tek bayide (5 araç) açık sefer bunun çok altındadır; 100'ü aşan tarihçede
    // en yeni seferler önce geldiği için açık kartlar kaybolmaz, yalnız kapanış özeti kısalır.
    queryFn: () => api.get<Page<Trip>>('/api/trips?dateFilter=all&pageSize=100'),
    refetchInterval: 15_000,
  });
}

/**
 * Bayat açık seferler (tripDate İstanbul bugününden eski, hâlâ açık). Sefer listesinin
 * `today`/`week` süzgeci bunları gizler; bu yüzden 'all' ile durum durum çekilir (açık sefer
 * sayısı azdır). Sunucudaki `isTripStale` ile aynı kural (lib/stale-trip.ts).
 */
export function useStaleOpenTrips(): Trip[] {
  const results = useQueries({
    queries: OPEN_TRIP_STATUSES.map((status) => {
      const qs = `dateFilter=all&status=${status}&pageSize=100`;
      return {
        queryKey: ['trips', qs],
        queryFn: () => api.get<Page<Trip>>(`/api/trips?${qs}`),
      };
    }),
  });
  const now = new Date();
  const today = istanbulDayString(now);
  const hour = istanbulHourOf(now);
  return results
    .flatMap((r) => r.data?.items ?? [])
    .filter((trip) => isStaleOpenTrip(trip, today, hour));
}

/**
 * Sefer KPI'ları — SUNUCUDAN, bütün döneme göre.
 *
 * Önceden KPI'lar listenin ilk sayfasından hesaplanıyordu ve "Tümü" filtresinde 100'den fazla
 * sefer olduğunda sessizce eksik çıkıyordu (Faz 2 denetimi U10). Aynı hata Faz 1'de müşterilerde
 * bulunmuştu; sayım sunucunun işidir.
 */
export function useTripSummary(dateFilter: string) {
  return useQuery({
    queryKey: ['trips-summary', dateFilter],
    queryFn: () => api.get<TripSummary>(`/api/trips/summary?dateFilter=${dateFilter}`),
  });
}

/** Sefer detayı (§3.10): defter satırları, denklem, km/mazot, teslimatlar. */
export function useTrip(id: string | null) {
  return useQuery({
    queryKey: ['trip', id],
    queryFn: () => api.get<TripDetail>(`/api/trips/${id!}`),
    enabled: id !== null,
  });
}

/** Onay bekleyen ara yükleme kuyruğu (§4.11). */
export function useReloadRequests(status = 'PENDING') {
  return useQuery({
    queryKey: ['reload-requests', status],
    queryFn: () => api.get<{ items: ReloadRequest[] }>(`/api/reload-requests?status=${status}`),
  });
}

/**
 * Sefer iptalinden SONRA tazelenecek sorgular.
 *
 * Liste geniştir çünkü iptalin kendisi geniştir: ters kayıtlar stoğa, cariye, zimmete, emanete
 * ve kasaya yazılır, siparişler `FAILED`'a döner, iptal makbuzları kesilir, araç sayacı
 * defterden yeniden hesaplanır (ADR 2026-09-09 §1). Dar tutmak ekranlar arası rakam
 * ayrışması demek olurdu — mazot dolumunda `cash-*` sorgularının da tazelenmesiyle aynı gerekçe.
 */
const VOID_AFFECTED_QUERIES = [
  'trips',
  'trips-summary',
  'trip',
  'reload-requests',
  'dashboard',
  'orders',
  'notifications',
  'approvals-count',
  'cash-summary',
  'cash-series',
  'cash-moves',
  'accounts-summary',
  'customers',
  'customer-txns',
  'customer-deposits',
  'driver-floats',
  'driver-floats-summary',
  'driver-performance',
  'receipts',
  'stock-levels',
  'stock-vehicles',
  'stock-moves',
  'stock-foreign',
  'report',
  'vehicles',
  'vehicle-positions',
] as const;

export function useTripMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['trips'] });
    await qc.invalidateQueries({ queryKey: ['trips-summary'] });
    await qc.invalidateQueries({ queryKey: ['trip'] });
    await qc.invalidateQueries({ queryKey: ['reload-requests'] });
    await qc.invalidateQueries({ queryKey: ['depot-day'] });
  };
  return {
    decideReload: useMutation({
      mutationFn: (v: { id: string; approve: boolean; rejectReason?: string }) =>
        api.post<ReloadRequest>(`/api/reload-requests/${v.id}/decide`, {
          approve: v.approve,
          rejectReason: v.rejectReason,
        }),
      onSuccess: invalidate,
    }),
    markVarianceRead: useMutation({
      mutationFn: (id: string) => api.post<Trip>(`/api/trips/${id}/variance-read`, {}),
      onSuccess: invalidate,
    }),
    /**
     * Seferi iptal et: silme değil, ters kayıt (ADR 2026-09-09). YALNIZ `OWNER`.
     *
     * Yetki asıl kapısı SUNUCUDADIR (`@Roles('OWNER')` + `RolesGuard`); ekranda düğmeyi
     * gizlemek yalnız gürültüyü azaltır. Ret sebepleri (nakit kasaya girmiş, zaten iptal,
     * durum uygun değil) sunucunun KENDİ metniyle döner ve `ApiError.message` üzerinden
     * kullanıcıya olduğu gibi gösterilir; genel hata mesajıyla yutulmaz.
     */
    voidTrip: useMutation({
      mutationFn: (v: { id: string; reason: string }) =>
        api.post<TripVoidResponse>(`/api/trips/${v.id}/void`, { reason: v.reason }),
      onSuccess: async () => {
        for (const key of VOID_AFFECTED_QUERIES) {
          await qc.invalidateQueries({ queryKey: [key] });
        }
      },
    }),
  };
}

export interface BulkAssignResult {
  assigned: string[];
  skipped: { orderId: string; reason: string }[];
}

export function useBulkAssign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderIds, vehicleId }: { orderIds: string[]; vehicleId: string }) =>
      api.post<BulkAssignResult>('/api/orders/bulk-assign', { orderIds, vehicleId }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['orders'] });
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useOrderMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['orders'] }),
      qc.invalidateQueries({ queryKey: ['dashboard'] }),
    ]);
  };

  const create = useMutation({
    mutationFn: (body: unknown) => api.post<Order>('/api/orders', body),
    onSuccess: invalidate,
  });
  const assign = useMutation({
    mutationFn: ({ id, vehicleId }: { id: string; vehicleId: string }) =>
      api.post<Order>(`/api/orders/${id}/assign`, { vehicleId }),
    onSuccess: invalidate,
  });
  const autoAssign = useMutation({
    mutationFn: (id: string) => api.post<Order>(`/api/orders/${id}/auto-assign`),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.post<Order>(`/api/orders/${id}/cancel`, { reason }),
    onSuccess: invalidate,
  });
  /** Müşterisiz açık siparişe müşteri bağlar (`POST /api/orders/:id/customer`). */
  const linkCustomer = useMutation({
    mutationFn: ({ id, customerId }: { id: string; customerId: string }) =>
      api.post<Order>(`/api/orders/${id}/customer`, { customerId }),
    onSuccess: invalidate,
  });
  return { create, assign, autoAssign, cancel, linkCustomer };
}

// ── Faz 3: para (kasa, cari, emanet, makbuz) ──

export function useCashSummary(date?: string) {
  return useQuery({
    queryKey: ['cash-summary', date ?? 'today'],
    queryFn: () =>
      api.get<CashSummary & { adjustment?: string; transfer?: string }>(
        `/api/cash/summary${date ? `?date=${date}` : ''}`,
      ),
  });
}

export function useCashSeries(days = 7) {
  return useQuery({
    queryKey: ['cash-series', days],
    queryFn: () => api.get<CashPoint[]>(`/api/cash/series?days=${days}`),
  });
}

export function useCashMoves(date?: string) {
  return useQuery({
    queryKey: ['cash-moves', date ?? 'today'],
    queryFn: () =>
      api.get<Page<CashMove>>(`/api/cash/moves?pageSize=100${date ? `&date=${date}` : ''}`),
  });
}

/** Kasa sayımı: tüm tutarlar sunucudan gelen 2 haneli dizelerdir; panelde aritmetik yok. */
export interface CashCountPreview {
  expected: string;
  counted: string;
  variance: string;
  /// İlk kasa sayımı mı: fark açılış düzeltmesi olarak yazılır (sunucu karar verir).
  isFirstCount: boolean;
}

export interface CashCountResult extends CashCountPreview {
  moveId: string | null;
}

/** Önizleme bir okuma ama kullanıcı tetikler (otomatik sorgu değil): mutation olarak kurulu. */
export function useCashCount() {
  const qc = useQueryClient();
  return {
    preview: useMutation({
      mutationFn: (counted: string) =>
        api.get<CashCountPreview>(`/api/cash/count/preview?counted=${encodeURIComponent(counted)}`),
    }),
    save: useMutation({
      mutationFn: (v: {
        counted: string;
        expected: string;
        note?: string;
        clientId: string;
        opening?: boolean;
      }) => api.post<CashCountResult>('/api/cash/count', v),
      onSuccess: async () => {
        await qc.invalidateQueries({ queryKey: ['cash-summary'] });
        await qc.invalidateQueries({ queryKey: ['cash-series'] });
        await qc.invalidateQueries({ queryKey: ['cash-moves'] });
      },
    }),
  };
}

export function useAccountsSummary() {
  return useQuery({
    queryKey: ['accounts-summary'],
    queryFn: () => api.get<AccountsSummary>('/api/customer-accounts/summary'),
  });
}

export function useDriverFloats(date?: string) {
  return useQuery({
    queryKey: ['driver-floats', date ?? 'open'],
    queryFn: () => api.get<DriverFloatRow[]>(`/api/driver-floats${date ? `?date=${date}` : ''}`),
  });
}

export function useFloatSummary() {
  return useQuery({
    queryKey: ['driver-floats-summary'],
    queryFn: () => api.get<FloatSummary>('/api/driver-floats/summary'),
  });
}

export function useReceipts(customerId?: string) {
  return useQuery({
    queryKey: ['receipts', customerId ?? 'all'],
    queryFn: () =>
      api.get<Page<Receipt>>(
        `/api/receipts?pageSize=50${customerId ? `&customerId=${customerId}` : ''}`,
      ),
  });
}

/** Tek makbuz (§3.18 makbuz modalı) — liste satırından daha ayrıntılı. */
export function useReceipt(id: string | null) {
  return useQuery({
    queryKey: ['receipt', id],
    queryFn: () => api.get<ReceiptDetail>(`/api/receipts/${id!}`),
    enabled: id !== null,
  });
}

/** Müşterinin cari hareketleri (§3.8 D.5) — defterden, sayfalı. */
export function useCustomerTxns(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-txns', customerId],
    queryFn: () => api.get<Page<CustomerTxn>>(`/api/customers/${customerId!}/txns?pageSize=25`),
    enabled: customerId !== null,
  });
}

/** Marka bazlı müşteri zimmeti (Faz 3.2). */
export function useCustomerDeposits(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-deposits', customerId],
    queryFn: () => api.get<CustomerDepositRow[]>(`/api/customers/${customerId!}/deposits`),
    enabled: customerId !== null,
  });
}

export function useFinanceMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['cash-summary'] });
    await qc.invalidateQueries({ queryKey: ['cash-series'] });
    await qc.invalidateQueries({ queryKey: ['cash-moves'] });
    await qc.invalidateQueries({ queryKey: ['accounts-summary'] });
    await qc.invalidateQueries({ queryKey: ['driver-floats'] });
    await qc.invalidateQueries({ queryKey: ['driver-floats-summary'] });
    await qc.invalidateQueries({ queryKey: ['receipts'] });
    await qc.invalidateQueries({ queryKey: ['customer-txns'] });
    await qc.invalidateQueries({ queryKey: ['customers'] });
  };
  return {
    collect: useMutation({
      mutationFn: (v: {
        customerId: string;
        amount: string;
        method: 'CASH' | 'CARD';
        clientId: string;
        /// Borcu aşan tutar yalnız AÇIK onayla yazılır; bayraksız istek 400 döner (B-08).
        allowAdvance?: boolean;
      }) =>
        api.post(`/api/customers/${v.customerId}/payments`, {
          amount: v.amount,
          method: v.method,
          clientId: v.clientId,
          allowAdvance: v.allowAdvance,
        }),
      onSuccess: invalidate,
    }),
    addExpense: useMutation({
      mutationFn: (v: { amount: string; kind: string; note?: string; clientId: string }) =>
        api.post('/api/cash/expenses', v),
      onSuccess: invalidate,
    }),
    addTransfer: useMutation({
      mutationFn: (v: {
        kind: 'OWNER_WITHDRAWAL' | 'BANK_DEPOSIT';
        amount: string;
        note?: string;
        clientId: string;
      }) => api.post('/api/cash/transfers', v),
      onSuccess: invalidate,
    }),
    confirmHandover: useMutation({
      mutationFn: (tripId: string) => api.post('/api/driver-floats/confirm', { tripId }),
      onSuccess: invalidate,
    }),
  };
}

// ── Faz 4: stok ──

export function useStockLevels() {
  return useQuery({
    queryKey: ['stock-levels'],
    queryFn: () => api.get<StockLevel[]>('/api/stock/levels'),
  });
}

export function useVehicleStock() {
  return useQuery({
    queryKey: ['stock-vehicles'],
    queryFn: () => api.get<VehicleStock[]>('/api/stock/vehicles'),
  });
}

export function useStockMoves(params: Record<string, string | undefined>) {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) query.set(k, v);
  const qs = query.toString();
  return useQuery({
    queryKey: ['stock-moves', qs],
    queryFn: () => api.get<Page<StockMove>>(`/api/stock/moves${qs ? `?${qs}` : ''}`),
  });
}

export function useForeignAccount() {
  return useQuery({
    queryKey: ['stock-foreign'],
    queryFn: () => api.get<ForeignAccount>('/api/stock/foreign'),
  });
}

/**
 * Onay rozeti sayısı.
 *
 * `/api/approvals/count` ucu OWNER/MANAGER'a açıktır (`@Roles('OWNER','MANAGER')`); DRIVER rolü
 * de panele girebildiği için sorgu `enabled` ile kapatılır — aksi halde şoför 403 alır ve
 * konsol/panelde gürültü çıkar. `enabled` verilmezse davranış ESKİSİ GİBİ kalır (true).
 */
export function useApprovalCount(enabled = true) {
  return useQuery({
    queryKey: ['approvals-count'],
    queryFn: () =>
      api.get<{ total: number; byKind: Record<string, number> }>('/api/approvals/count'),
    enabled,
  });
}

export function useStockMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['stock-levels'] });
    await qc.invalidateQueries({ queryKey: ['stock-vehicles'] });
    await qc.invalidateQueries({ queryKey: ['stock-moves'] });
    await qc.invalidateQueries({ queryKey: ['stock-foreign'] });
  };
  return {
    receiveShipment: useMutation({
      mutationFn: (v: {
        brandId: string;
        productId: string;
        qtyFull: number;
        qtyEmptyOut: number;
        waybillNo?: string;
        clientId: string;
      }) => api.post('/api/stock/shipments', v),
      onSuccess: invalidate,
    }),
    settleForeign: useMutation({
      mutationFn: (v: {
        brandName: string;
        qty: number;
        kind: 'SWAPPED' | 'RETURNED';
        clientId: string;
      }) => api.post('/api/stock/foreign/settle', v),
      onSuccess: invalidate,
    }),
    adjust: useMutation({
      mutationFn: (v: {
        productId: string;
        location: 'DEPOT' | 'VEHICLE';
        vehicleId?: string;
        condition: 'FULL' | 'EMPTY';
        qty: number;
        reason: string;
        clientId: string;
      }) => api.post('/api/stock/adjust', v),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Canlı araç konumları (§3.11).
 *
 * 15 saniyede bir yenilenir — prototipin belirttiği aralık. Daha sık sormak şoförün pilini
 * değil sunucuyu yorar; daha seyrek sormak haritayı "donmuş" gösterir.
 */
export function useVehiclePositions() {
  return useQuery({
    queryKey: ['vehicle-positions'],
    queryFn: () => api.get<VehiclePosition[]>('/api/tracking/positions'),
    refetchInterval: 15_000,
  });
}

// ── Faz 6: kanallar ──

export function useChannels() {
  return useQuery({ queryKey: ['channels'], queryFn: () => api.get<Channel[]>('/api/channels') });
}

export function useMappings(filter?: string) {
  return useQuery({
    queryKey: ['channel-mappings', filter ?? 'all'],
    queryFn: () =>
      api.get<ProductMapping[]>(`/api/channels/mappings${filter ? `?filter=${filter}` : ''}`),
  });
}

export function useUnmappedCount() {
  return useQuery({
    queryKey: ['channel-unmapped'],
    queryFn: () => api.get<{ count: number }>('/api/channels/mappings/unmapped-count'),
  });
}

export function useScrapers() {
  return useQuery({
    queryKey: ['scrapers'],
    queryFn: () => api.get<ScraperStatus[]>('/api/integrations/scrapers'),
  });
}

export function useScraperMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['scrapers'] });
    // Ayarlar'daki bayrak durumu (FlagStatus) bu yazmadan sonra bayat kalmasın.
    await qc.invalidateQueries({ queryKey: ['tenant'] });
  };
  return {
    toggle: useMutation({
      mutationFn: (v: { source: string; enabled: boolean }) =>
        api.post(`/api/integrations/scrapers/${v.source}/toggle`, { enabled: v.enabled }),
      onSuccess: invalidate,
    }),
    /**
     * Hayat geçmiş sipariş aktarımı (`Sipariş Sorgulama`). `from`/`to` `YYYY-MM-DD`, uçlar
     * dahil. `dryRun: true` hiçbir şey yazmaz, yalnız rapor döner. Uygulanınca sipariş listesi
     * tazelenir.
     */
    importHistory: useMutation({
      mutationFn: (v: { source: PullSource; from: string; to: string; dryRun: boolean }) =>
        api.post<HistoryImportResult>(`/api/integrations/${v.source}/import/history`, {
          from: v.from,
          to: v.to,
          dryRun: v.dryRun,
        }),
      onSuccess: async (_r, v) => {
        if (!v.dryRun) await qc.invalidateQueries({ queryKey: ['orders'] });
      },
    }),
    /**
     * Master-data import (Hayat panel) — ürün/fiyat karşılaştırma. `dryRun=true` ile rapor
     * alınır; kullanıcı onaylarsa `dryRun=false` ile yalnız eşleşen + fiyatı farklı olan
     * ürünlerin `unitPrice` kolonu güncellenir. `create` ile eşleşmeyen adlar YENİ ürün
     * olarak açılabilir (round 3); `dryRun=true` iken YOK SAYILIR.
     */
    importMasterData: useMutation({
      mutationFn: (v: { source: PullSource; dryRun: boolean; create?: string[] }) =>
        api.post<ImportReport>(`/api/integrations/${v.source}/import/products`, {
          dryRun: v.dryRun,
          ...(v.create && v.create.length > 0 ? { create: v.create } : {}),
        }),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Eşleme kartındaki "Yeni ürün olarak ekle" düğmesinin altında yatan mutasyon. Sunucu tek
 * `$transaction` ile marka + ürün + eşleme bağlar. Başarılıysa products/mappings cache'i
 * tazelenir.
 */
export function useCreateProductFromMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      id: string;
      body: {
        name: string;
        kind: 'JUG_19L' | 'JUG_15L_GLASS' | 'CASE_05L';
        unitPrice: string;
        depositAmount?: string;
      };
    }) =>
      api.post<{ product: { id: string; name: string } }>(
        `/api/channels/mappings/${v.id}/create-product`,
        v.body,
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['products'] });
      await qc.invalidateQueries({ queryKey: ['channel-mappings'] });
      await qc.invalidateQueries({ queryKey: ['channel-unmapped'] });
    },
  });
}

/** Eşleme için öneri (temizlenmiş ad + tür tahmini + biliniyorsa son fiyat). */
export function useMappingSuggestion(id: string | null) {
  return useQuery({
    queryKey: ['mapping-suggestion', id],
    queryFn: () => api.get<MappingSuggestion>(`/api/channels/mappings/${id}/suggestion`),
    enabled: Boolean(id),
  });
}

export function useChannelMutations() {
  const qc = useQueryClient();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['channels'] });
    await qc.invalidateQueries({ queryKey: ['channel-mappings'] });
    await qc.invalidateQueries({ queryKey: ['channel-unmapped'] });
  };
  return {
    /** Giriş bilgisi/yapılandırma kaydı. Çekim BAŞLATMAZ; durum PENDING kalır (§5). */
    saveConnection: useMutation({
      mutationFn: (v: {
        source: PullSource;
        credentials?: { username: string; password: string };
      }) => api.post('/api/channels/connections', v),
      onSuccess: invalidate,
    }),
    /** Soğuk test fazında sunucu 403 döner (§0); buton zaten kapalı, uç ikinci kapıdır. */
    activate: useMutation({
      mutationFn: (v: { source: PullSource; consentNote: string }) =>
        api.post(`/api/channels/${v.source}/activate`, {
          consent: true,
          consentNote: v.consentNote,
        }),
      onSuccess: invalidate,
    }),
    disable: useMutation({
      mutationFn: (source: PullSource) => api.post(`/api/channels/${source}/disable`, {}),
      onSuccess: invalidate,
    }),
    mapProduct: useMutation({
      mutationFn: (v: {
        id: string;
        productId: string | null;
        ledgerPolicy?: LedgerPolicy | null;
      }) =>
        api.patch(`/api/channels/mappings/${v.id}`, {
          productId: v.productId,
          ...(v.ledgerPolicy !== undefined ? { ledgerPolicy: v.ledgerPolicy } : {}),
        }),
      onSuccess: invalidate,
    }),
  };
}

// ── Gelişmiş ayarlar + deneme çekimi ──

export function useChannelConfig(source: string | undefined) {
  return useQuery({
    queryKey: ['channel-config', source],
    queryFn: () => api.get<ChannelConfig>(`/api/channels/${source}/config`),
    enabled: Boolean(source),
  });
}

export function useSaveChannelConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { source: PullSource; config: Record<string, unknown> }) =>
      api.post('/api/channels/connections', v),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

export function useDryRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { source: PullSource; consentNote?: string }) =>
      api.post<DryRunResult>(`/api/integrations/scrapers/${v.source}/dry-run`, {
        ...(v.consentNote ? { consentNote: v.consentNote } : {}),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

/**
 * Manuel oturum bağlama/koparma (Getir gibi CAPTCHA'lı paneller için).
 *
 * `bind` PUT /api/channels/:source/session; gövdede JETON gider, sunucu prob (dryRun)
 * ile doğrular ve AES-256-GCM şifreli yazar. `unbind` DELETE aynı ucu; 401'de de
 * sunucu otomatik siler. Yanıt yalnız `{ state, expiresAt }` — JETON İÇERMEZ; panel
 * `token` alanını gönderdikten sonra bellekten/dom'dan/logdan HEMEN siler, bu yüzden
 * `useManualSession` da state'te jeton tutmaz.
 *
 * Başarı mesajı (toast) ve kanal listesi tazelemesi hook seviyesinde tetiklenir; form
 * kapanır ya da unmount olursa bile toast kaybolmaz. Jeton mutation cache'te
 * `gcTime: 0` ile TUTULMAZ — DOM/form/state sıfırlansa bile react-query belleğinde
 * kalmamalı; taze oturum için cache boşalmış olur.
 */
export function useManualSession() {
  const qc = useQueryClient();
  const toast = useToast();
  const invalidate = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['channels'] });
  };
  return {
    bind: useMutation({
      mutationFn: (v: { source: PullSource; token: string; consentNote?: string }) =>
        api.put<ChannelSession>(`/api/channels/${v.source}/session`, {
          token: v.token,
          ...(v.consentNote ? { consentNote: v.consentNote } : {}),
        }),
      gcTime: 0,
      onSuccess: async () => {
        await invalidate();
        toast(t('ch.manualSession.ui.connected'));
      },
    }),
    unbind: useMutation({
      mutationFn: (v: { source: PullSource }) =>
        api.delete<ChannelSession>(`/api/channels/${v.source}/session`),
      gcTime: 0,
      onSuccess: invalidate,
    }),
  };
}

// ── Faz 7: raporlar ──

export function useReport(period: string) {
  return useQuery({
    queryKey: ['report', period],
    queryFn: () => api.get<ReportSummary>(`/api/reports?period=${period}`),
  });
}

// ── Panelde kapanan siparişleri deftere işleme (panel-kapanan defter akışı) ──

/**
 * Önizleme sorgusu — seçilen gün aralığı için READY/HOLD/POSTED satırları + özet.
 * Yalnız OWNER çağırabilir (sunucu rol denetimi); panelde de koşul.
 */
export function useExternalLedgerPreview(from: string | null, to: string | null, enabled = true) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  return useQuery({
    queryKey: ['external-ledger-preview', from, to],
    queryFn: () =>
      api.get<ExternalLedgerPreview>(`/api/orders/external-ledger/preview${qs ? `?${qs}` : ''}`),
    enabled: enabled && from !== null && to !== null,
  });
}

/**
 * Önizleme sonrası seçilen siparişleri deftere işle. `acceptNegative` yalnız depo eksiye
 * düşecekse kullanıcının onay kutusu işaretlemesiyle `true` gider; aksi halde POST 400 döner
 * (`externalLedger.negativeNeedsAccept`).
 */
export function usePostExternalLedger() {
  const qc = useQueryClient();
  const invalidateAll = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['external-ledger-preview'] });
    await qc.invalidateQueries({ queryKey: ['orders'] });
    await qc.invalidateQueries({ queryKey: ['order'] });
    await qc.invalidateQueries({ queryKey: ['dashboard'] });
    await qc.invalidateQueries({ queryKey: ['stock-levels'] });
    await qc.invalidateQueries({ queryKey: ['stock-vehicles'] });
    await qc.invalidateQueries({ queryKey: ['stock-moves'] });
    await qc.invalidateQueries({ queryKey: ['report'] });
    await qc.invalidateQueries({ queryKey: ['accounts-summary'] });
    await qc.invalidateQueries({ queryKey: ['cash-summary'] });
    await qc.invalidateQueries({ queryKey: ['customer-txns'] });
    await qc.invalidateQueries({ queryKey: ['customer-deposits'] });
  };
  return useMutation({
    mutationFn: (v: { orderIds: string[]; acceptNegative?: boolean }) =>
      api.post<ExternalLedgerPostResponse>('/api/orders/external-ledger/post', {
        orderIds: v.orderIds,
        acceptNegative: v.acceptNegative,
      }),
    onSuccess: invalidateAll,
  });
}

/**
 * Tek bir POSTED siparişin defter kaydını ters kayıtla geri alır. Sebep metni (10..300)
 * zorunlu; sunucu 409 ile işlenmemiş/ikinci kez denemeyi reddeder.
 */
export function useReverseExternalLedger() {
  const qc = useQueryClient();
  const invalidateAll = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['external-ledger-preview'] });
    await qc.invalidateQueries({ queryKey: ['orders'] });
    await qc.invalidateQueries({ queryKey: ['order'] });
    await qc.invalidateQueries({ queryKey: ['dashboard'] });
    await qc.invalidateQueries({ queryKey: ['stock-levels'] });
    await qc.invalidateQueries({ queryKey: ['stock-vehicles'] });
    await qc.invalidateQueries({ queryKey: ['stock-moves'] });
    await qc.invalidateQueries({ queryKey: ['report'] });
    await qc.invalidateQueries({ queryKey: ['accounts-summary'] });
    await qc.invalidateQueries({ queryKey: ['cash-summary'] });
    await qc.invalidateQueries({ queryKey: ['customer-txns'] });
    await qc.invalidateQueries({ queryKey: ['customer-deposits'] });
  };
  return useMutation({
    mutationFn: (v: { orderId: string; reason: string }) =>
      api.post<{ ok: true }>(`/api/orders/${v.orderId}/external-ledger/reverse`, {
        reason: v.reason,
      }),
    onSuccess: invalidateAll,
  });
}
